// Ported from tavily-platform-poc src/components/layout/glyph-backdrop.tsx.
import { useEffect, useRef } from "react";
import { GLYPH_RAMP, GLYPH_SCENE } from "./glyph-scene";

const SCENE_WIDTH = 2560;
const SCENE_HEIGHT = 1440;
const CELL = 14.68;
const FONT = '500 14px "JetBrains Mono Variable", monospace';
const RADIUS = 240;
const SCRAMBLE_RADIUS = RADIUS * 0.6;
const OFFSET_X = -1.16;
const OFFSET_Y = 6.34;

const ROWS = GLYPH_SCENE.length;
const COLUMNS = Math.max(...GLYPH_SCENE.map((row) => row.length));
const SCENE = GLYPH_SCENE.flatMap((row, y) =>
  Array.from(row, (glyph, x) => ({
    glyph,
    x: x * CELL + OFFSET_X,
    y: y * CELL + OFFSET_Y,
    column: x,
    row: y,
    shade: GLYPH_RAMP.indexOf(glyph),
  })).filter(({ shade }) => shade > 0),
);
const CELL_AT = new Int32Array(ROWS * COLUMNS).fill(-1);
SCENE.forEach((cell, i) => (CELL_AT[cell.row * COLUMNS + cell.column] = i));

const THEME_PROPERTIES = [
  "--glyph-ink-start",
  "--glyph-ink-middle",
  "--glyph-ink-end",
  "--glyph-opacity",
  "--glyph-highlight-opacity",
];

function themeKey(style: CSSStyleDeclaration) {
  return THEME_PROPERTIES.map((name) => style.getPropertyValue(name)).join();
}

export default function GlyphBackdrop() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas: HTMLCanvasElement = canvasRef.current;
    const context = canvas.getContext("2d")!;
    // Both buffers are drawn from characters. No image is loaded or sampled.
    const scene = document.createElement("canvas");
    const sceneContext = scene.getContext("2d")!;
    // The scene with scrambled cells applied; `scene` stays pristine so each
    // cell can be restored by copying its box back.
    const live = document.createElement("canvas");
    const liveContext = live.getContext("2d")!;
    const highlight = document.createElement("canvas");
    const highlightContext = highlight.getContext("2d")!;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const target = { x: 0, y: 0, strength: 0 };
    const current = { x: 0, y: 0, strength: 0 };
    let width = 0;
    let height = 0;
    let dpr = 1;
    let scale = 1;
    let left = 0;
    let canvasLeft = 0;
    let canvasTop = 0;
    let theme = "";
    let needsComposite = false;
    // Eased pointer speed in [0, 1]; only movement scrambles the grid.
    let motion = 0;
    const cellX = new Float32Array(SCENE.length);
    const cellY = new Float32Array(SCENE.length);
    const revertAt = new Float64Array(SCENE.length);
    const shuffleAt = new Float64Array(SCENE.length);
    const scrambled = new Set<number>();
    let frame = 0;
    let previousTime = 0;
    let disposed = false;
    let pointerInside = false;
    let sceneOpacity = 0;
    let highlightOpacity = 0;

    // Calibrate ink coverage once so different glyphs retain the scene's tones.
    const swatch = document.createElement("canvas");
    swatch.width = swatch.height = 20;
    const swatchContext = swatch.getContext("2d", { willReadFrequently: true })!;
    function measureGlyphs() {
      swatchContext.font = FONT;
      swatchContext.textAlign = "center";
      swatchContext.textBaseline = "middle";
      return Array.from(GLYPH_RAMP, (glyph, shade) => {
        swatchContext.clearRect(0, 0, 20, 20);
        swatchContext.fillText(glyph, 10, 10);
        const pixels = swatchContext.getImageData(0, 0, 20, 20).data;
        let coverage = 0;
        for (let i = 3; i < pixels.length; i += 4) coverage += pixels[i] / 255;
        const tone = (shade / (GLYPH_RAMP.length - 1)) * (55 / 255);
        return Math.min(1, (tone * CELL * CELL) / (coverage || 1));
      });
    }
    let alphaForGlyph = measureGlyphs();

    function cellBox(i: number): [number, number, number, number] {
      const half = (CELL * scale) / 2;
      const x0 = Math.round((cellX[i] - half) * dpr);
      const y0 = Math.round((cellY[i] - half) * dpr);
      const x1 = Math.round((cellX[i] + half) * dpr);
      const y1 = Math.round((cellY[i] + half) * dpr);
      return [x0, y0, x1 - x0, y1 - y0];
    }

    function restoreCell(i: number) {
      const [x, y, w, h] = cellBox(i);
      liveContext.clearRect(x / dpr, y / dpr, w / dpr, h / dpr);
      copy(liveContext, scene, x, y, w, h);
    }

    function shuffleCell(i: number) {
      const [x, y, w, h] = cellBox(i);
      const shade = 1 + Math.floor(Math.random() * (GLYPH_RAMP.length - 1));
      liveContext.clearRect(x / dpr, y / dpr, w / dpr, h / dpr);
      liveContext.save();
      // Tall glyphs overflow the cell; clipping keeps reverts from leaving
      // stray ink on neighbouring cells.
      liveContext.beginPath();
      liveContext.rect(x / dpr, y / dpr, w / dpr, h / dpr);
      liveContext.clip();
      // Rescale so the random glyph keeps the original cell's tone.
      liveContext.globalAlpha = Math.min(1, (alphaForGlyph[shade] * SCENE[i].shade) / shade);
      liveContext.fillText(GLYPH_RAMP[shade], cellX[i], cellY[i]);
      liveContext.restore();
    }

    function scramble(time: number) {
      // The wake shrinks as the pointer slows, so it reverts from the edges in.
      const radius = SCRAMBLE_RADIUS * current.strength * Math.sqrt(motion);
      if (motion > 0.02) {
        const span = radius / scale / CELL;
        const column = ((current.x - left) / scale - OFFSET_X) / CELL;
        const row = (current.y / scale - OFFSET_Y) / CELL;
        const firstRow = Math.max(0, Math.floor(row - span));
        const lastRow = Math.min(ROWS - 1, Math.ceil(row + span));
        const firstColumn = Math.max(0, Math.floor(column - span));
        const lastColumn = Math.min(COLUMNS - 1, Math.ceil(column + span));
        for (let r = firstRow; r <= lastRow; r++) {
          for (let c = firstColumn; c <= lastColumn; c++) {
            const i = CELL_AT[r * COLUMNS + c];
            if (i < 0) continue;
            const dx = cellX[i] - current.x;
            const dy = cellY[i] - current.y;
            if (dx * dx + dy * dy > radius * radius) continue;
            // A staggered linger dissolves the trail back to the scene
            // instead of snapping the whole wake at once.
            revertAt[i] = time + 200 + Math.random() * 700;
            if (!scrambled.has(i)) {
              scrambled.add(i);
              shuffleAt[i] = 0;
            }
          }
        }
      }
      let changed = false;
      for (const i of scrambled) {
        if (time >= revertAt[i]) {
          restoreCell(i);
          scrambled.delete(i);
          changed = true;
        } else if (time >= shuffleAt[i]) {
          shuffleCell(i);
          shuffleAt[i] = time + (60 + Math.random() * 180) / Math.max(motion, 0.2);
          changed = true;
        }
      }
      return changed;
    }

    function draw(time: number) {
      frame = 0;
      const elapsed = Math.max(0, time - previousTime);
      previousTime = time;
      const follow = 1 - Math.exp(-elapsed / 180);
      const fade = 1 - Math.exp(-elapsed / 240);
      const stepX = (target.x - current.x) * follow;
      const stepY = (target.y - current.y) * follow;
      current.x += stepX;
      current.y += stepY;
      current.strength += (target.strength - current.strength) * fade;
      const speed = elapsed > 0 ? Math.hypot(stepX, stepY) / elapsed : 0;
      const targetMotion = Math.min(1, speed / 0.6);
      // Picks up quickly, winds down slowly.
      const ease = targetMotion > motion ? 80 : 450;
      motion += (targetMotion - motion) * (1 - Math.exp(-elapsed / ease));
      const settling =
        Math.abs(target.x - current.x) > 0.1 ||
        Math.abs(target.y - current.y) > 0.1 ||
        Math.abs(target.strength - current.strength) > 0.001 ||
        motion > 0.01;
      if (!settling || reducedMotion.matches) {
        motion = 0;
        current.x = target.x;
        current.y = target.y;
        current.strength = reducedMotion.matches ? 0 : target.strength;
      }

      const changed = scramble(time);
      if (settling || changed || needsComposite) composite();
      if ((settling || scrambled.size > 0) && !reducedMotion.matches && !document.hidden) {
        frame = requestAnimationFrame(draw);
      }
    }

    // Offscreen buffers can be larger than the canvas, so every copy names its
    // source rectangle in device pixels.
    function copy(
      into: CanvasRenderingContext2D,
      source: HTMLCanvasElement,
      x: number,
      y: number,
      w: number,
      h: number,
    ) {
      into.drawImage(source, x, y, w, h, x / dpr, y / dpr, w / dpr, h / dpr);
    }

    function composite() {
      needsComposite = false;
      context.clearRect(0, 0, width, height);
      context.globalAlpha = sceneOpacity;
      copy(context, live, 0, 0, canvas.width, canvas.height);
      // The highlight is empty beyond RADIUS, so only its box is redrawn.
      const x0 = Math.max(0, Math.floor((current.x - RADIUS) * dpr));
      const y0 = Math.max(0, Math.floor((current.y - RADIUS) * dpr));
      const x1 = Math.min(canvas.width, Math.ceil((current.x + RADIUS) * dpr));
      const y1 = Math.min(canvas.height, Math.ceil((current.y + RADIUS) * dpr));
      if (current.strength > 0 && x1 > x0 && y1 > y0) {
        const [bx, by, bw, bh] = [x0 / dpr, y0 / dpr, (x1 - x0) / dpr, (y1 - y0) / dpr];
        const gradient = highlightContext.createRadialGradient(
          current.x,
          current.y,
          0,
          current.x,
          current.y,
          RADIUS,
        );
        gradient.addColorStop(0, `rgba(0,0,0,${current.strength * 0.9})`);
        gradient.addColorStop(0.35, `rgba(0,0,0,${current.strength * 0.65})`);
        gradient.addColorStop(0.7, `rgba(0,0,0,${current.strength * 0.18})`);
        gradient.addColorStop(1, "rgba(0,0,0,0)");
        highlightContext.save();
        // source-in clears everything outside the drawn image; the clip keeps
        // that work inside the box.
        highlightContext.beginPath();
        highlightContext.rect(bx, by, bw, bh);
        highlightContext.clip();
        highlightContext.clearRect(bx, by, bw, bh);
        highlightContext.fillStyle = gradient;
        highlightContext.beginPath();
        highlightContext.arc(current.x, current.y, RADIUS, 0, Math.PI * 2);
        highlightContext.fill();
        highlightContext.globalCompositeOperation = "source-in";
        copy(highlightContext, live, x0, y0, x1 - x0, y1 - y0);
        highlightContext.restore();
        context.globalAlpha = highlightOpacity;
        copy(context, highlight, x0, y0, x1 - x0, y1 - y0);
      }
    }

    function schedule() {
      if (!frame && !disposed && !document.hidden) {
        previousTime = performance.now();
        frame = requestAnimationFrame(draw);
      }
    }

    function paintScene() {
      scale = Math.max(width / SCENE_WIDTH, height / SCENE_HEIGHT);
      left = width - SCENE_WIDTH * scale;
      const style = getComputedStyle(canvas);
      // In dev, Vite can inject style.css after the first paint; until then the
      // ink variables are empty and addColorStop('') throws.
      if (!style.getPropertyValue("--glyph-ink-start").trim()) {
        if (!disposed) requestAnimationFrame(paintScene);
        return;
      }
      theme = themeKey(style);
      sceneOpacity = Number(style.getPropertyValue("--glyph-opacity"));
      highlightOpacity = Number(style.getPropertyValue("--glyph-highlight-opacity"));
      // Mineral inks, matching the platform's light theme. Darker, muted hues
      // keep their color over the pale sky; pale tints wash out.
      for (const glyphContext of [sceneContext, liveContext]) {
        const ink = glyphContext.createLinearGradient(0, 0, width, height);
        ink.addColorStop(0, style.getPropertyValue("--glyph-ink-start").trim());
        ink.addColorStop(0.5, style.getPropertyValue("--glyph-ink-middle").trim());
        ink.addColorStop(1, style.getPropertyValue("--glyph-ink-end").trim());
        glyphContext.fillStyle = ink;
        glyphContext.font = FONT.replace("14px", `${14 * scale}px`);
        glyphContext.textAlign = "center";
        glyphContext.textBaseline = "middle";
      }
      sceneContext.clearRect(0, 0, width, height);
      SCENE.forEach((cell, i) => {
        const x = (cellX[i] = left + cell.x * scale);
        const y = (cellY[i] = cell.y * scale);
        if (x < -CELL || x > width + CELL || y > height + CELL) return;
        sceneContext.globalAlpha = alphaForGlyph[cell.shade];
        sceneContext.fillText(cell.glyph, x, y);
      });
      liveContext.clearRect(0, 0, width, height);
      liveContext.globalAlpha = 1;
      copy(liveContext, scene, 0, 0, canvas.width, canvas.height);
      scrambled.clear();
      needsComposite = true;
      // Resizing clears the visible canvas. Repaint before the browser displays
      // this frame so continuous resizing never exposes an empty backdrop.
      cancelAnimationFrame(frame);
      draw(performance.now());
    }

    function resize() {
      const rect = canvas.getBoundingClientRect();
      canvasLeft = rect.left;
      canvasTop = rect.top;
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Offscreen buffers only grow, so window resizes don't reallocate them.
      const bufferWidth = Math.ceil(Math.max(width, window.innerWidth) * dpr);
      const bufferHeight = Math.ceil(Math.max(height, window.innerHeight) * dpr);
      for (const buffer of [scene, live, highlight]) {
        if (buffer.width < bufferWidth) buffer.width = bufferWidth;
        if (buffer.height < bufferHeight) buffer.height = bufferHeight;
        buffer.getContext("2d")!.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      paintScene();
    }

    function leave() {
      pointerInside = false;
      target.strength = 0;
      schedule();
    }

    function move(event: PointerEvent) {
      if (event.pointerType === "touch" || reducedMotion.matches) return;
      const x = event.clientX - canvasLeft;
      const y = event.clientY - canvasTop;
      if (x < 0 || x > width || y < 0 || y > height) {
        if (pointerInside) leave();
        return;
      }
      target.x = x;
      target.y = y;
      target.strength = 1;
      // Fade in where the pointer enters, rather than sweeping across the page.
      if (!pointerInside) {
        current.x = x;
        current.y = y;
      }
      pointerInside = true;
      schedule();
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    const themeObserver = new MutationObserver(() => {
      if (themeKey(getComputedStyle(canvas)) === theme) return;
      pointerInside = false;
      target.strength = current.strength = 0;
      paintScene();
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    window.addEventListener("pointermove", move, { passive: true });
    document.documentElement.addEventListener("pointerleave", leave);
    window.addEventListener("blur", leave);
    document.addEventListener("visibilitychange", leave);
    reducedMotion.addEventListener("change", leave);
    resize();
    void document.fonts.load(FONT).then(() => {
      if (disposed) return;
      alphaForGlyph = measureGlyphs();
      paintScene();
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      themeObserver.disconnect();
      window.removeEventListener("pointermove", move);
      document.documentElement.removeEventListener("pointerleave", leave);
      window.removeEventListener("blur", leave);
      document.removeEventListener("visibilitychange", leave);
      reducedMotion.removeEventListener("change", leave);
    };
  }, []);

  return <canvas ref={canvasRef} className="glyph-backdrop" aria-hidden="true" />;
}
